import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Package, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import RateAppPrompt, { useRatePrompt } from '@/components/RateAppPrompt';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_ATTEMPTS = 5;

// Status Components
const LoadingStatus = () => (
  <div className="text-center py-12">
    <Loader2 className="w-16 h-16 text-[#2563EB] animate-spin mx-auto mb-6" />
    <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Archivo, sans-serif' }}>
      Processing Payment
    </h2>
    <p className="text-muted-foreground">
      Please wait while we confirm your payment...
    </p>
  </div>
);

const SuccessStatus = ({ paymentInfo }) => (
  <div className="text-center py-12">
    <div className="w-20 h-20 rounded-full bg-[#22c55e]/10 flex items-center justify-center mx-auto mb-6">
      <CheckCircle className="w-12 h-12 text-[#22c55e]" />
    </div>
    <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Archivo, sans-serif' }}>
      Payment Successful!
    </h2>
    <p className="text-muted-foreground mb-6">
      Thank you for your purchase. Your order has been confirmed.
    </p>
    
    {paymentInfo && (
      <Card className="max-w-sm mx-auto mb-8">
        <CardContent className="pt-6">
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Amount Paid</span>
              <span className="font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                ${(paymentInfo.amount_total / 100).toFixed(2)} {paymentInfo.currency?.toUpperCase()}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Status</span>
              <span className="text-[#22c55e] font-medium capitalize">
                {paymentInfo.payment_status}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    )}
    
    <div className="flex flex-col sm:flex-row gap-4 justify-center">
      <Link to="/products" data-testid="continue-shopping-success">
        <Button size="lg" className="gap-2">
          <Package className="w-5 h-5" />
          Continue Shopping
        </Button>
      </Link>
    </div>
  </div>
);

const ExpiredStatus = () => (
  <div className="text-center py-12">
    <div className="w-20 h-20 rounded-full bg-[#eab308]/10 flex items-center justify-center mx-auto mb-6">
      <XCircle className="w-12 h-12 text-[#eab308]" />
    </div>
    <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Archivo, sans-serif' }}>
      Session Expired
    </h2>
    <p className="text-muted-foreground mb-6">
      Your payment session has expired. Please try again.
    </p>
    <Link to="/cart" data-testid="return-to-cart-expired">
      <Button size="lg" className="gap-2">
        Return to Cart
        <ArrowRight className="w-4 h-4" />
      </Button>
    </Link>
  </div>
);

const TimeoutStatus = () => (
  <div className="text-center py-12">
    <div className="w-20 h-20 rounded-full bg-[#eab308]/10 flex items-center justify-center mx-auto mb-6">
      <Loader2 className="w-12 h-12 text-[#eab308]" />
    </div>
    <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Archivo, sans-serif' }}>
      Payment Processing
    </h2>
    <p className="text-muted-foreground mb-6">
      Your payment is still being processed. Please check your email for confirmation.
    </p>
    <Link to="/products" data-testid="continue-shopping-timeout">
      <Button size="lg" className="gap-2">
        Continue Shopping
        <ArrowRight className="w-4 h-4" />
      </Button>
    </Link>
  </div>
);

const ErrorStatus = () => (
  <div className="text-center py-12">
    <div className="w-20 h-20 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-6">
      <XCircle className="w-12 h-12 text-destructive" />
    </div>
    <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Archivo, sans-serif' }}>
      Something Went Wrong
    </h2>
    <p className="text-muted-foreground mb-6">
      We couldn&apos;t verify your payment. Please contact support if you were charged.
    </p>
    <div className="flex flex-col sm:flex-row gap-4 justify-center">
      <Link to="/cart" data-testid="return-to-cart-error">
        <Button size="lg" variant="outline">
          Return to Cart
        </Button>
      </Link>
      <Link to="/products" data-testid="continue-shopping-error">
        <Button size="lg" className="gap-2">
          Continue Shopping
          <ArrowRight className="w-4 h-4" />
        </Button>
      </Link>
    </div>
  </div>
);

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const gateway = searchParams.get('gateway');
  const paystackReference = searchParams.get('reference') || searchParams.get('trxref');
  
  const [status, setStatus] = useState('loading');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [attempts, setAttempts] = useState(0);
  
  // Rate app prompt
  const { showPrompt, checkAndShowPrompt, incrementPurchaseCount, closePrompt } = useRatePrompt();

  // Increment purchase count and check if should show rating prompt
  useEffect(() => {
    if (status === 'success') {
      incrementPurchaseCount();
      // Delay showing the rate prompt by 3 seconds after success
      const timer = setTimeout(() => {
        checkAndShowPrompt();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [status, incrementPurchaseCount, checkAndShowPrompt]);

  // Verify Paystack payment
  const verifyPaystackPayment = useCallback(async () => {
    if (!paystackReference) {
      setStatus('error');
      return;
    }

    try {
      const response = await axios.get(`${API}/paystack/verify/${paystackReference}`);
      
      if (response.data.success && response.data.status === 'success') {
        setPaymentInfo({
          amount_total: response.data.amount * 100, // Convert to cents for consistency
          currency: response.data.currency,
          payment_status: 'paid'
        });
        setStatus('success');
      } else {
        setStatus('error');
      }
    } catch (err) {
      console.error('Error verifying Paystack payment:', err);
      setStatus('error');
    }
  }, [paystackReference]);

  const pollPaymentStatus = useCallback(async () => {
    // Handle Paystack verification
    if (gateway === 'paystack' || paystackReference) {
      verifyPaystackPayment();
      return;
    }

    // Handle Stripe verification
    if (!sessionId) {
      setStatus('error');
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      setPaymentInfo(response.data);

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling if still pending
      if (attempts < MAX_ATTEMPTS) {
        setTimeout(() => setAttempts(prev => prev + 1), 2000);
      } else {
        setStatus('timeout');
      }
    } catch (err) {
      console.error('Error checking payment status:', err);
      if (attempts < MAX_ATTEMPTS) {
        setTimeout(() => setAttempts(prev => prev + 1), 2000);
      } else {
        setStatus('error');
      }
    }
  }, [sessionId, attempts, gateway, paystackReference, verifyPaystackPayment]);

  useEffect(() => {
    pollPaymentStatus();
  }, [pollPaymentStatus]);

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return <LoadingStatus />;
      case 'success':
        return <SuccessStatus paymentInfo={paymentInfo} />;
      case 'expired':
        return <ExpiredStatus />;
      case 'timeout':
        return <TimeoutStatus />;
      case 'error':
      default:
        return <ErrorStatus />;
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="checkout-success-page">
      <Card>
        <CardContent className="p-8">
          {renderContent()}
        </CardContent>
      </Card>
      
      {/* Rate App Prompt - shows after successful purchase */}
      {showPrompt && <RateAppPrompt onClose={closePrompt} />}
    </div>
  );
};

export default CheckoutSuccessPage;
